/**
 * Phase 6 — Assistant Draft panel (Architect + Admin views).
 * Reusable panel embedded in ArchitectRequestsPage & AdminArchitectRequestsPage.
 *
 * Props:
 *   requestId      string
 *   role           'architect' | 'admin'
 *   status         request status (assigned/accepted/in_progress/…)
 *   architectApproved bool (only for role='architect')
 *   onRefresh      optional callback after actions
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, CheckCircle2, XCircle, Loader2, ShieldAlert } from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_LABELS = {
  plain_english_program_summary: 'Plain-English Program Summary',
  kickback_structure_considerations: 'Kickback Structure Considerations',
  utility_and_benefit_considerations: 'Utility and Benefit Considerations',
  holder_experience_considerations: 'Holder Experience Considerations',
  compliance_and_language_risk_callouts: 'Compliance and Language Risk Callouts',
  questions_architect_should_ask: 'Questions the Architect Should Ask',
  missing_information_checklist: 'Missing Information Checklist',
  suggested_safe_public_wording: 'Suggested Safe Public Wording',
};

const cardStyle = {
  background: theme.colors.bg.card, border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.card, padding: 16, marginTop: 12,
  boxShadow: theme.colors.shadows.sm,
};

export default function AssistantDraftPanel({ requestId, role = 'architect', status, architectApproved = true, onRefresh }) {
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }), []);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [acting, setActing] = useState(false);
  const [notes, setNotes] = useState('');

  const basePath = role === 'admin'
    ? `${API}/admin/architect-requests/${requestId}/assistant-draft`
    : `${API}/architect/requests/${requestId}/assistant-draft`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(basePath, auth);
      setDraft(res.data?.assistant_draft || null);
    } catch (e) {
      // 403 for unassigned is expected in some flows — do not toast.
      if (e?.response?.status !== 403) toast.error(e?.response?.data?.detail || 'Failed to load draft');
    } finally { setLoading(false); }
  }, [basePath, auth]);

  useEffect(() => { load(); }, [load]);

  const canGenerate = role === 'admin'
    || (architectApproved && ['assigned', 'accepted', 'in_progress'].includes(status));

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(basePath, {}, { ...auth, timeout: 120000 });
      setDraft(res.data?.assistant_draft || null);
      toast.success('Assistant draft generated');
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Draft generation failed');
    } finally { setGenerating(false); }
  };

  const review = async (action) => {
    setActing(true);
    try {
      await axios.patch(`${basePath}/review`, { action, architect_notes: notes || null }, auth);
      toast.success(`Draft ${action}ed`);
      setNotes('');
      await load();
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Review failed');
    } finally { setActing(false); }
  };

  if (loading) {
    return (
      <div data-testid={`assistant-draft-loading-${requestId}`} style={{ ...cardStyle, color: theme.colors.text.muted }}>
        Loading assistant draft…
      </div>
    );
  }

  return (
    <div data-testid={`assistant-draft-panel-${requestId}`} style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Sparkles size={18} color={theme.colors.primary} />
        <div style={{ fontWeight: 800, fontSize: 15 }}>Assistant Draft — Requires Architect Review</div>
        {draft?.status && (
          <span data-testid={`draft-status-${draft.status}`} style={{
            padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 800, textTransform: 'uppercase',
            background: `${theme.colors.status.info}1A`, color: theme.colors.status.info,
          }}>{draft.status}</span>
        )}
      </div>
      <div style={{ marginTop: 6, fontSize: 12, color: theme.colors.text.muted }}>
        This draft is generated to help organize review notes. It is not legal, tax, financial, investment,
        or compliance advice. Do not use it as final customer-facing language without qualified review.
      </div>

      {!draft && (
        <div style={{ marginTop: 10 }}>
          {canGenerate ? (
            <button data-testid={`draft-generate-btn-${requestId}`} disabled={generating} onClick={generate}
              style={{ padding: '9px 14px', borderRadius: theme.radius.sm, border: 'none',
                background: theme.colors.primary, color: '#fff', cursor: 'pointer', fontWeight: 700,
                display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {generating ? <><Loader2 size={14} className="animate-spin" /> Generating…</> : <><Sparkles size={14} /> Generate Assistant Draft</>}
            </button>
          ) : (
            <div data-testid={`draft-gen-blocked-${requestId}`} style={{ fontSize: 12, color: theme.colors.text.muted }}>
              Draft generation is available only for approved Architects on assigned/accepted/in_progress requests.
            </div>
          )}
        </div>
      )}

      {draft && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 11, color: theme.colors.text.muted, marginBottom: 6 }}>
            Generated: {draft.generated_at && new Date(draft.generated_at).toLocaleString()} · Model: {draft.model}
            {draft.regenerated_at && <> · Regenerated: {new Date(draft.regenerated_at).toLocaleString()}</>}
          </div>

          {Array.isArray(draft.risk_flags) && draft.risk_flags.length > 0 && (
            <div data-testid={`draft-risk-flags-${requestId}`} style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
              {draft.risk_flags.map((f, i) => (
                <span key={i} style={{ padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 800,
                  background: '#FEF2F2', color: '#991B1B', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <ShieldAlert size={10} /> {f}
                </span>
              ))}
            </div>
          )}

          <div style={{ display: 'grid', gap: 10 }}>
            {Object.keys(SECTION_LABELS).map(k => {
              const val = draft.draft?.[k];
              if (!val || (Array.isArray(val) && val.length === 0)) return null;
              return (
                <div key={k} data-testid={`draft-section-${k}`}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: theme.colors.text.primary, marginBottom: 4 }}>
                    {SECTION_LABELS[k]}
                  </div>
                  {Array.isArray(val) ? (
                    <ul style={{ margin: '0 0 0 18px', padding: 0, fontSize: 13, color: theme.colors.text.secondary }}>
                      {val.map((x, i) => <li key={i} style={{ marginBottom: 2 }}>{x}</li>)}
                    </ul>
                  ) : (
                    <div style={{ fontSize: 13, color: theme.colors.text.secondary }}>{val}</div>
                  )}
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: 10, padding: 10, background: theme.colors.bg.secondary,
            borderRadius: theme.radius.sm, fontSize: 11, color: theme.colors.text.muted }}>
            {draft.disclaimer}
          </div>

          {role === 'architect' && architectApproved && draft.status === 'generated' && (
            <div style={{ marginTop: 12 }}>
              <textarea data-testid={`draft-review-notes-${requestId}`}
                placeholder="Architect notes on this draft (optional)…"
                value={notes} onChange={e => setNotes(e.target.value)}
                style={{ width: '100%', padding: '9px 11px', border: `1px solid ${theme.colors.borders}`,
                  borderRadius: theme.radius.sm, fontSize: 13, minHeight: 50, fontFamily: 'inherit' }} />
              <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button data-testid={`draft-accept-btn-${requestId}`} disabled={acting} onClick={() => review('accept')}
                  style={{ padding: '8px 12px', borderRadius: theme.radius.sm, border: 'none',
                    background: theme.colors.status.success, color: '#fff', cursor: 'pointer', fontWeight: 700,
                    display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <CheckCircle2 size={14} /> Accept Draft
                </button>
                <button data-testid={`draft-reject-btn-${requestId}`} disabled={acting} onClick={() => review('reject')}
                  style={{ padding: '8px 12px', borderRadius: theme.radius.sm, border: 'none',
                    background: theme.colors.status.error, color: '#fff', cursor: 'pointer', fontWeight: 700,
                    display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <XCircle size={14} /> Reject Draft
                </button>
                <button data-testid={`draft-regen-btn-${requestId}`} disabled={generating} onClick={generate}
                  style={{ padding: '8px 12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borders}`,
                    background: theme.colors.bg.card, cursor: 'pointer', fontWeight: 700,
                    display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {generating ? 'Regenerating…' : 'Regenerate'}
                </button>
              </div>
            </div>
          )}

          {role === 'admin' && (
            <div style={{ marginTop: 12 }}>
              <button data-testid={`draft-admin-regen-btn-${requestId}`} disabled={generating} onClick={generate}
                style={{ padding: '8px 12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borders}`,
                  background: theme.colors.bg.card, cursor: 'pointer', fontWeight: 700 }}>
                {generating ? 'Regenerating…' : 'Regenerate (admin)'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
